import { downloadList } from '../list/list-export';
import { NumberedPager } from '@/components/list/NumberedPager';
import { ListExportButton } from '@/components/list/ListExportButton';
import { Plus } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { PageHead } from '@/admin-area/ui/PageHead';
import { PhonePrimary } from '@/admin-area/ui/PhonePrimary';
import { ListToolbar } from '@/components/list/ListToolbar';
import type { ListView } from '@/components/list/ViewTabs';
import { Button } from '@/components/ui/button';
import { AuditorDownload, ReadOnlyMarker } from '../closing/AuditorBar';
import {
  EXPENSES_APPROVE,
  isReadOnlyAuditor,
  useExpensesPermissions,
} from '../use-expenses-permissions';
import { ExportNotesButton } from '../exports/ExportButtons';
import { NewReportDialog } from './NewReportDialog';
import { ReportsContent } from './ReportsContent';
import { useReportsView } from './use-reports-view';
import { useOnboardingClick } from '@/communications/use-onboarding-click';

/** «Note»: the reports the caller may see (spec §1.1, §1.2; the board's «le note»). */
export function ReportsListPage() {
  // The email's button lands here with `?onboarding=<step>`: logged once, then removed (#739).
  useOnboardingClick();
  const { t } = useTranslation('expenses');
  const { t: tc } = useTranslation('common');
  const titleId = useId();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const view = useReportsView();
  const { t: tr } = useTranslation('expenses-reports');
  const permissions = useExpensesPermissions();
  const readOnly = isReadOnlyAuditor(permissions);
  const mayApprove =
    permissions.status === 'ready' && permissions.permissions.includes(EXPENSES_APPROVE);
  const views: ListView[] = [
    { id: 'drafts', label: tr('views.drafts'), params: { status: 'draft' } },
    { id: 'sentBack', label: tr('views.sentBack'), params: { status: 'more_info_requested' } },
  ];
  const canCreate = view.canCreate && !readOnly;

  const newReport = (
    <Button type="button" onClick={() => setCreating(true)}>
      <Plus />
      {t('reports.newButton')}
    </Button>
  );

  const primary = canCreate ? <span className="page-primary">{newReport}</span> : undefined;
  // #915 F4: Gestione and the Revisore download the notes file for the list's year and owner.
  const exportNotes = (
    <ExportNotesButton
      period={view.active['period'] ?? null}
      owner={view.showOwner ? view.params.get('owner') : null}
    />
  );
  const headActions = (
    <>
      {exportNotes}
      {readOnly ? <ReadOnlyMarker /> : primary}
    </>
  );

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      <PageHead
        titleId={titleId}
        title={t('reports.title')}
        summary={view.showOwner ? t('reports.summaryAll') : t('reports.summaryOwn')}
        actions={headActions}
      />
      {view.params.get('purpose') ? (
        <div className="bg-muted rounded-lg p-3 text-sm">
          <p>{t('configuration.reportsScope', { label: view.params.get('purposeLabel') ?? '' })}</p>
          <Button variant="ghost" className="min-h-11" onClick={() => view.clear()}>
            {t('configuration.clearPurpose')}
          </Button>
        </div>
      ) : null}
      <ListToolbar
        label={t('reports.title')}
        sort={{
          label: tc('list.sort'),
          value: view.query.sort ? `${view.query.sort}:${view.query.direction}` : 'default',
          onChange: (value) => view.set('sort', value === 'default' ? null : value),
          options: [
            { value: 'default', label: tc('list.defaultSort') },
            ...(['period', 'number', 'title', 'total', 'status'] as const).flatMap((field) =>
              (['asc', 'desc'] as const).map((direction) => ({
                value: `${field}:${direction}`,
                label: `${t(`reports.columns.${field}`)} · ${tc(direction === 'asc' ? 'list.ascending' : 'list.descending')}`,
              })),
            ),
          ],
        }}
        search={{ label: tr('search'), value: view.q, onWrite: (v) => view.set('q', v) }}
        fields={view.fields}
        active={view.active}
        onFilter={view.onFilter}
        onClear={view.clear}
        views={{
          label: tr('views.label'),
          items: views,
          current: view.params,
          onSelect: (picked) => view.set('status', picked.params['status'] ?? null),
        }}
        pageSize={{ value: view.pageSize, onChange: view.setPageSize }}
      />
      {mayApprove ? (
        <Link to="/expenses/approvals" className="text-sm font-medium underline">
          {tr('views.toApprove')}
        </Link>
      ) : null}
      <ListExportButton
        onExport={(format) => downloadList('/v1/expenses/reports/export', view.query, format)}
      />
      <ReportsContent
        pageSize={view.pageSize}
        state={view.state}
        shown={view.shown}
        desktop={view.desktop}
        phone={view.phone}
        narrowed={view.narrowed}
        showOwner={view.showOwner}
        newReport={canCreate ? newReport : undefined}
        onClear={view.clear}
        onReload={view.reload}
      />
      {view.state.status === 'ready' ? (
        <NumberedPager
          page={view.page}
          pageSize={view.pageSize}
          total={view.state.total}
          onPage={view.setPage}
        />
      ) : null}
      {readOnly ? <AuditorDownload month={view.creationMonth} /> : null}
      {canCreate ? <PhonePrimary>{newReport}</PhonePrimary> : null}
      <NewReportDialog
        open={creating}
        period={view.creationMonth}
        onClose={() => setCreating(false)}
        onCreated={(report) => void navigate(`/expenses/reports/${encodeURIComponent(report.id)}`)}
      />
    </section>
  );
}
